import { type ContrastCheck, type RampTheme, nudgeLightness } from './brandContrast'
import { contrastRatio } from './brandPalette'
import { type Oklch, hexToOklch, maxChromaFor, oklchToHex } from './oklch'

interface CanvasToken {
  name: string
  stock: Record<RampTheme, string>
  chroma: Record<RampTheme, number>
}

const CANVAS: readonly CanvasToken[] = [
  {
    name: '--color-background',
    stock: { light: '#f7f8fb', dark: '#0a0c12' },
    chroma: { light: 0.003, dark: 0.005 },
  },
  {
    name: '--color-surface',
    stock: { light: '#ffffff', dark: '#12151d' },
    chroma: { light: 0.0015, dark: 0.0045 },
  },
  {
    name: '--color-border',
    stock: { light: '#e8ebf1', dark: '#262d38' },
    chroma: { light: 0.0075, dark: 0.008 },
  },
  {
    name: '--color-control-border',
    stock: { light: '#798495', dark: '#5d6573' },
    chroma: { light: 0.01, dark: 0.01 },
  },
  {
    name: '--color-control-hover',
    stock: { light: '#566173', dark: '#798495' },
    chroma: { light: 0.011, dark: 0.01 },
  },
  {
    name: '--color-text',
    stock: { light: '#151a22', dark: '#e8ebf1' },
    chroma: { light: 0.008, dark: 0.005 },
  },
  {
    name: '--color-text-muted',
    stock: { light: '#566173', dark: '#a9b2bf' },
    chroma: { light: 0.011, dark: 0.01 },
  },
  {
    name: '--color-text-subtle',
    stock: { light: '#798495', dark: '#798495' },
    chroma: { light: 0.01, dark: 0.01 },
  },
  {
    name: '--color-neutral-50',
    stock: { light: '#fafbfc', dark: '#0c1016' },
    chroma: { light: 0.003, dark: 0.0045 },
  },
  {
    name: '--color-neutral-100',
    stock: { light: '#f3f5f8', dark: '#151a22' },
    chroma: { light: 0.0055, dark: 0.0045 },
  },
  {
    name: '--color-neutral-200',
    stock: { light: '#e8ebf1', dark: '#262d38' },
    chroma: { light: 0.0075, dark: 0.008 },
  },
  {
    name: '--color-neutral-300',
    stock: { light: '#d6dce4', dark: '#3d4754' },
    chroma: { light: 0.0075, dark: 0.008 },
  },
  {
    name: '--color-neutral-400',
    stock: { light: '#a9b2bf', dark: '#566173' },
    chroma: { light: 0.01, dark: 0.01 },
  },
  {
    name: '--color-neutral-500',
    stock: { light: '#798495', dark: '#798495' },
    chroma: { light: 0.01, dark: 0.01 },
  },
  {
    name: '--color-neutral-600',
    stock: { light: '#566173', dark: '#a9b2bf' },
    chroma: { light: 0.011, dark: 0.01 },
  },
  {
    name: '--color-neutral-700',
    stock: { light: '#3d4754', dark: '#d6dce4' },
    chroma: { light: 0.008, dark: 0.005 },
  },
  {
    name: '--color-neutral-800',
    stock: { light: '#262d38', dark: '#e8ebf1' },
    chroma: { light: 0.008, dark: 0.005 },
  },
  {
    name: '--color-neutral-900',
    stock: { light: '#151a22', dark: '#f3f5f8' },
    chroma: { light: 0.008, dark: 0.005 },
  },
  {
    name: '--color-neutral-950',
    stock: { light: '#0c1016', dark: '#fafbfc' },
    chroma: { light: 0.008, dark: 0.005 },
  },
]

const TEXT_TOKENS = new Set(['--color-text', '--color-text-muted', '--color-text-subtle'])
const BACKDROP_TOKENS = ['--color-background', '--color-surface'] as const
const CONTROL_BORDER = '--color-control-border'
const CONTROL_BORDER_FLOOR = 3

export const CANVAS_TOKEN_NAMES: readonly string[] = CANVAS.map((token) => token.name)

function tint(token: CanvasToken, brand: Oklch, theme: RampTheme): Oklch {
  const stock = hexToOklch(token.stock[theme])
  return {
    l: stock.l,
    c: Math.min(token.chroma[theme], brand.c, maxChromaFor(stock.l, brand.h)),
    h: brand.h,
  }
}

function stockOf(name: string, theme: RampTheme): string {
  const token = CANVAS.find((candidate) => candidate.name === name)
  if (token === undefined) {
    throw new Error(`no canvas token ${name}`)
  }
  return token.stock[theme]
}

function contrastChecks(
  name: string,
  theme: RampTheme,
  tokens: Record<string, string>
): ContrastCheck[] {
  const surface = tokens['--color-surface'] ?? stockOf('--color-surface', theme)
  if (name === CONTROL_BORDER) {
    const stockRatio = contrastRatio(stockOf(name, theme), stockOf('--color-surface', theme))
    return [{ against: surface, floor: Math.max(CONTROL_BORDER_FLOOR, stockRatio) }]
  }
  if (!TEXT_TOKENS.has(name)) {
    return []
  }
  return BACKDROP_TOKENS.map((backdrop) => ({
    against: tokens[backdrop] ?? stockOf(backdrop, theme),
    floor: contrastRatio(stockOf(name, theme), stockOf(backdrop, theme)),
  }))
}

export function brandCanvasTokens(brand: Oklch, theme: RampTheme): Record<string, string> {
  const tokens: Record<string, string> = {}
  for (const token of CANVAS) {
    tokens[token.name] = oklchToHex(tint(token, brand, theme))
  }
  for (const token of CANVAS) {
    const checks = contrastChecks(token.name, theme, tokens)
    if (checks.length > 0) {
      tokens[token.name] = nudgeLightness(tint(token, brand, theme), checks)
    }
  }
  return tokens
}
